import Settings from '../domain/Settings';
import {getTCData, hasPurposesConsent, hasVendorsConsent} from '../services/privacyService';
import {getGigyaId} from '../utils/gigya';
import logger from '../utils/logger';
import * as performance from '../utils/performance';
import {getPwId} from '../utils/pwid-util';
// Library adds 100kb to our build size
import ID5, {ID5InitConfig, ID5Instance} from '@id5io/id5-api.js';

export type ID5Identity = {
	gpid?: string;
	linkType?: number;
};

const DEFAULT_AVAILABLE_TIMEOUT_MS = 300;

let identity: ID5Identity = undefined,
	idTracked = false;

export function init(): void {
	const settings = Settings.getInstance();

	if (!settings.features?.id5)
		return;

	if (!settings.id5?.partnerId) {
		logger.error('Tried loading ID5 without partner id, skipping.');
		return;
	}

	if (!hasRequiredConsent())
		return;

	try {
		performance.mark('id5 - start');
		idTracked = false;

		const id5Config = getId5Config(settings.id5.partnerId);

		if (!id5Config)
			return;

		initializeID5(id5Config, settings.id5.availableTimeoutMs ?? DEFAULT_AVAILABLE_TIMEOUT_MS);
	} catch (e) {
		logger.error('Something went wrong processing ID5', e);
	} finally {
		performance.mark('id5 - end');
	}
}

export function getIdentity(): ID5Identity | undefined {
	return identity ? {...identity} : undefined;
}

function hasRequiredConsent(): boolean {
	return hasPurposesConsent([1, 3, 4]) && hasVendorsConsent([131]);
}

function getId5Config(partnerId: number): ID5InitConfig | null {
	const tcData = getTCData(),
		partnerData = getPartnerData();

	if (Object.keys(partnerData).length === 0) {
		logger.warn(`No ID5 partner data found, skipping.`);
		return null;
	}

	return {
		partnerId,
		cmpApi: 'static',
		consentData: {
			getTCData: tcData
		},
		partnerData
	} satisfies ID5InitConfig;
}

function getPartnerData(): NonNullable<ID5InitConfig['partnerData']> {
	return Object.fromEntries(
		Object.entries({
			puid: getPwId(),
			xpuid: getGigyaId() || undefined,
		}).filter(([, v]) => v)
	);
}

function initializeID5(config: ID5InitConfig, availableTimeoutMs: number): void {
	const instance = ID5.init(config);

	instance.onAvailable(_onStatusUpdate, availableTimeoutMs);
	instance.onUpdate(_onStatusUpdate);
}

function _onStatusUpdate(instance: ID5Instance) {
	const gpid = instance.getGpId(),
		linkType = instance.getLinkType();

	identity = gpid ? {
		gpid,
		linkType,
	} : undefined;

	trackIdentity(identity);
}

function trackIdentity(identityToTrack: ID5Identity): void {
	if (
		!Settings.getInstance().id5?.trackIdentity
		|| !identityToTrack?.gpid
		|| idTracked
	) {
		return;
	}

	if (Array.isArray(window.dataLayer)) {
		idTracked = true;
		window.dataLayer.push({
			event: 'id5_available',
			id5_connect_id: identityToTrack.gpid,
			id5_link_type: `${identityToTrack.linkType}`,
		});
	} else {
		logger.warn('Could not send ID5 identity tracking event, no datalayer found.');
	}
}

